import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Cookie Policy — Apex AV" },
      { name: "description", content: "Apex AV cookie policy: what cookies we use, why we use them, and how you can control them." },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Cookie Policy — Apex AV" },
      { property: "og:description", content: "What cookies Apex AV uses and how to control them." },
      { property: "og:url", content: "https://apexav.com/cookies" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Cookie Policy — Apex AV" },
      { name: "twitter:description", content: "What cookies Apex AV uses and how to control them." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/cookies" }],
  }),
  component: CookiePage,
});

function CookiePage() {
  const updated = "October 20, 2026";

  return (
    <div className="container mx-auto px-6 py-16 md:py-24 max-w-3xl">
      <header className="mb-10">
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
          Cookie Policy — How Apex AV Uses Cookies
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {updated}</p>
      </header>

      <div className="prose prose-invert max-w-none space-y-8 text-muted-foreground leading-relaxed">
        <section>
          <p>
            This Cookie Policy explains how Apex AV ("we," "us," or "our") uses cookies and
            similar tracking technologies on apexav.com. It should be read alongside our{" "}
            <a href="/privacy" className="text-brand-text underline underline-offset-2">Privacy Policy</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">What are cookies?</h2>
          <p>
            Cookies are small text files placed on your device when you visit a website. They
            help sites function properly, remember your preferences, and measure how the site
            is used.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Types of cookies we use</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-foreground">Strictly necessary</strong> — Required for
              the site to function (e.g., remembering your cookie consent choice). These are
              always on.
            </li>
            <li>
              <strong className="text-foreground">Analytics</strong> — Help us understand how
              visitors use the site (pages viewed, traffic sources, device types) via Google
              Tag Manager and Google Analytics. Set only with your consent.
            </li>
            <li>
              <strong className="text-foreground">Marketing</strong> — Google may receive device,
              page, approximate location and cookie data to measure and optimize advertising.
              Tracking is blocked until acceptance in the EEA, UK, Switzerland and Canada
              (Canada is treated conservatively because our country lookup cannot identify Quebec).
              Unknown locations are also blocked. Elsewhere ordinary advertising measurement
              may run without a banner, unless you decline. No customer matching data is enabled by this setup.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Managing your preferences</h2>
          <p>
            Use Cookie settings in the footer to accept or decline at any time. We show the
            banner where consent is required or your location cannot be determined. Declining
            blocks optional Google tracking; withdrawing after tracking has loaded refreshes
            the page to stop it. Choices also apply across open tabs. Analytics requires acceptance.
            Your browser stores a consent record with a random visitor reference, your choice,
            time, region classification and the notice version; a later withdrawal preserves
            earlier records. Clearing browser storage removes these records and preferences.
          </p>
        </section>


        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Analytics in more detail</h2>
          <p>
            When analytics cookies are active, they typically record which pages you visit, how
            long you stay, the general type of device and browser you're using, and an
            approximate location based on IP address — not your name or exact address. We use
            this aggregate information to see which pages and service areas get the most
            interest and to fix pages that aren't working well, rather than to track individual
            visitors.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">How long cookies last</h2>
          <p>
            Session cookies are deleted automatically when you close your browser. Persistent
            cookies, including your cookie-consent preference and analytics identifiers, remain
            on your device for a set period (commonly up to 24 months, depending on the
            provider) or until you clear them manually. Clearing your browser's cookies at any
            time removes both types.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Opting out</h2>
          <p>
            Beyond the on-site banner, most browsers let you block all cookies, block only
            third-party cookies, or delete existing cookies from your privacy or security
            settings. Google also offers browser add-ons and account-level controls to limit
            how Analytics data is collected across the sites you visit. Blocking non-essential
            cookies will not prevent you from browsing or contacting us — it only limits the
            usage data we're able to see.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Third-party cookies</h2>
          <p>
            Some cookies are set by third-party services we use, including Google Tag
            Manager and Google Analytics. These providers have their own privacy and cookie
            policies. Read how Google uses this information at{" "}
            <a href="https://business.safety.google/privacy/" className="text-brand-text underline underline-offset-2" target="_blank" rel="noopener noreferrer">Google Privacy &amp; Terms</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Changes to this policy</h2>
          <p>
            We may update this Cookie Policy from time to time. The "Last updated" date at
            the top reflects the most recent changes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Contact us</h2>
          <p>
            Questions about cookies? Reach out to Apex AV at{" "}
            <a href="mailto:info@apexav.com" className="text-brand-text underline underline-offset-2">
              info@apexav.com
            </a>{" "}
            or{" "}
            <a href="tel:+13033173233" className="text-brand-text underline underline-offset-2">
              (303) 317-3233
            </a>.
          </p>
        </section>
      </div>
    </div>
  );
}
